import {
  Box,
  Button,
  Input,
  Text,
  Heading,
  HStack,
  useToast,
} from "@chakra-ui/react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { sendEmailCode, signup } from "../../src/api";

export default function JoinPage() {
  const navigate = useNavigate();
  const toast = useToast();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [authCode, setAuthCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [loadingCode, setLoadingCode] = useState(false);
  const [loadingJoin, setLoadingJoin] = useState(false);

  // 이메일 정규식
  const isValidEmail = (value) =>
    /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(value);

  // 비밀번호 정규식 (8~16자, 영문 + 숫자 조합)
  const isValidPassword = (value) =>
    /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d!@#$%^&*()_+\-={}[\]|;:'",.<>/?]{8,16}$/.test(
      value
    );

  // 📍 인증번호 요청
  const handleSendCode = async () => {
    if (!isValidEmail(email)) {
      toast({
        title: "잘못된 이메일",
        description: "올바른 이메일 주소를 입력해주세요.",
        status: "warning",
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    setLoadingCode(true);
    try {
      await sendEmailCode(email);
      toast({
        title: "인증번호 발송 완료",
        description: "입력한 이메일로 인증번호가 전송되었습니다.",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (e) {
      toast({
        title: "발송 실패",
        description: e?.response?.data?.message ?? e?.message,
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    } finally {
      setLoadingCode(false);
    }
  };

  // 📍 회원가입 실행
  const handleJoin = async () => {
    if (!name || !email || !password || !confirmPw || !authCode) {
      toast({
        title: "입력 오류",
        description: "전체 필수 항목을 입력해주세요.",
        status: "warning",
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    if (!isValidEmail(email)) {
      toast({
        title: "잘못된 이메일",
        description: "올바른 이메일 주소를 입력해주세요.",
        status: "warning",
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    if (!isValidPassword(password)) {
      toast({
        title: "비밀번호 형식 오류",
        description: "비밀번호는 8~16자리 영문+숫자 조합이어야 합니다.",
        status: "warning",
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    if (password !== confirmPw) {
      toast({
        title: "비밀번호 불일치",
        description: "비밀번호가 일치하지 않습니다.",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    setLoadingJoin(true);

    const body = {
      email,
      password,
      displayName: name,
      role: "admin",
      consents: [
        {
          slug: "privacy",
          revisionId: 2,
          consented: true,
        },
        {
          slug: "tos",
          revisionId: 1,
          consented: true,
        },
      ],
      locale: "ko",
      channel: "web",
      verificationCode: authCode,
      agree: true,
    };

    try {
      await signup(body);
      toast({
        title: "회원가입 완료",
        status: "success",
        duration: 2000,
        isClosable: true,
      });

      navigate("/login");
    } catch (error) {
      toast({
        title: "회원가입 실패",
        description: error.response?.data?.message || "다시 시도해주세요.",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    } finally {
      setLoadingJoin(false);
    }
  };

  return (
    <Box
      minH="100vh"
      bgGradient="linear(to-b, white, blue.50)"
      display="flex"
      alignItems="center"
      justifyContent="center"
      p="20px"
    >
      <Box w="632px" bg="white" p="40px" borderRadius="20px" boxShadow="lg">
        <Heading fontSize="24px" mb="40px">
          회원가입
        </Heading>

        {/* 이름 */}
        <Text fontSize="14px" fontWeight="600" mb="6px">
          이름{" "}
          <Text as="span" color="red.500">
            (필수)
          </Text>
        </Text>
        <Input
          placeholder="ex) 홍길동"
          mb="20px"
          value={name}
          onChange={(e) => setName(e.target.value)}
          h="46px"
        />

        {/* 이메일 인증 */}
        <Text fontSize="14px" fontWeight="600" mb="6px">
          이메일 인증{" "}
          <Text as="span" color="red.500">
            (필수)
          </Text>
        </Text>
        <HStack mb="10px">
          <Input
            placeholder="ex) example@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            h="46px"
          />
          <Button
            h="46px"
            minW="90px"
            bg="#0054C0"
            color="white"
            fontSize="14px"
            onClick={handleSendCode}
            isLoading={loadingCode}
          >
            인증 번호
          </Button>
        </HStack>
        <Input
          placeholder="인증번호 입력"
          mb="20px"
          value={authCode}
          onChange={(e) => setAuthCode(e.target.value)}
          h="46px"
        />

        {/* 비밀번호 */}
        <Text fontSize="14px" fontWeight="600" mb="6px">
          비밀번호{" "}
          <Text as="span" color="red.500">
            (필수)
          </Text>
        </Text>
        <Input
          placeholder="8-16자리 / 영문, 숫자 조합"
          type="password"
          mb="20px"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          h="46px"
        />

        {/* 비밀번호 확인 */}
        <Text fontSize="14px" fontWeight="600" mb="6px">
          비밀번호 확인{" "}
          <Text as="span" color="red.500">
            (필수)
          </Text>
        </Text>
        <Input
          placeholder="한 번 더 입력"
          type="password"
          mb="30px"
          value={confirmPw}
          onChange={(e) => setConfirmPw(e.target.value)}
          h="46px"
        />

        {/* 가입하기 버튼 */}
        <Button
          w="200px"
          h="54px"
          bg="#0054C0"
          color="white"
          fontSize="16px"
          display="block"
          mx="auto"
          onClick={handleJoin}
          isLoading={loadingJoin}
        >
          가입하기
        </Button>
      </Box>
    </Box>
  );
}
